import React, { useRef, useState } from 'react';
import { IconButton, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { mdiChevronDown, mdiChevronUp, mdiClose } from '@mdi/js';
import { categorizeImportDocuments } from '../../../util/ImportUtils';
import { labelFontSemiBold, vars } from '../../../Theme';
import Icon from '@mdi/react';
import ChipWithTooltip from '../Chip/ChipWithTooltip';
import ContextMenu from '../ContextMenu/ContextMenu';
import AppTooltip from '../AppTooltip/AppTooltip';
import { StructureViewMode } from '../../../types';

export interface ICategoryFields {
    Id: string;
    FullPath: string;
}

const ctgCompareFn = (a: ICategoryFields, b: ICategoryFields): number => {
    if (a.FullPath === b.FullPath) {
        return 0;
    }
    return a.FullPath > b.FullPath ? 1 : -1;
};

export default ({
    replicaId,
    documentId,
    value,
    mode,
}: {
    replicaId: string;
    documentId: string;
    value: string | string[];
    mode: StructureViewMode;
}) => {
    const chevronRef = useRef<HTMLButtonElement>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const processValue = (s: string) => {
        if (!s?.trim() || s.trim() === '|') {
            return { subjects: [], ids: [] };
        }

        return {
            subjects: s.split('|')[0].split('&&'),
            ids: s.split('|')[1].split('&&'),
        };
    };

    const { subjects, ids } = processValue(value as string);

    const categories: ICategoryFields[] = ids
        .map((id, index) => ({
            Id: id,
            FullPath: subjects[index],
        }))
        .sort(ctgCompareFn);

    const onOpenMenu = () => {
        setAnchorEl(chevronRef.current);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onRemoveCategory = (ctg: ICategoryFields) => {
        handleClose();

        if (ctg.FullPath && ctg.Id) {
            categorizeImportDocuments(replicaId, [documentId], [ctg], 'remove', mode);
        }
    };

    const contextMenuOption = (category: ICategoryFields) => (
        <Stack key={`option-${category.Id}`} direction='row' sx={{ width: '100%' }}>
            <ListItem sx={{ p: '2px 12px' }}>
                <ListItemText
                    primary={
                        <Typography
                            variant='body2'
                            sx={{
                                fontSize: 12,
                            }}
                        >
                            {category.FullPath}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                ...labelFontSemiBold,
                                fontSize: '16px',
                                color: vars.font,
                                fontWeight: 500,
                            }}
                        >
                            {category.FullPath.split('\\').pop()}
                        </Typography>
                    }
                />
            </ListItem>
            <IconButton
                onClick={() => onRemoveCategory(category)}
                sx={{ width: '34px', height: '34px', margin: 'auto' }}
            >
                <Icon path={mdiClose} size={1.0} />
            </IconButton>
        </Stack>
    );

    const contextMenu = () => (
        <>
            <IconButton ref={chevronRef} onClick={onOpenMenu} sx={{ p: '5px' }}>
                <Icon path={anchorEl ? mdiChevronUp : mdiChevronDown} size={1.0} />
            </IconButton>
            <ContextMenu
                sx={{ '& .MuiList-root': { padding: '0px' } }}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {categories.map(category => contextMenuOption(category))}
            </ContextMenu>
        </>
    );

    if (!categories?.length) {
        return <></>;
    }

    return (
        <Stack
            direction='row'
            sx={{
                alignItems: 'center',
                width: '100%',
            }}
        >
            <AppTooltip title={categories[0].FullPath}>
                <Typography
                    variant='body2'
                    sx={{
                        width: '100%',
                        fontSize: 14,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        paddingRight: '12px',
                    }}
                >
                    {categories[0].FullPath}
                </Typography>
            </AppTooltip>
            {categories.length > 1 && (
                <ChipWithTooltip
                    label={`+${categories.length - 1}`}
                    onClick={onOpenMenu}
                    chipSx={{ height: '24px', margin: 'auto' }}
                />
            )}
            {categories.length > 0 && contextMenu()}
        </Stack>
    );
};
